import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from './ToastProvider';
import Button from './Button';
import Stepper from './Stepper';
import RarityBadge from '@/components/RarityBadge';
import ShippingInputPanel from './ShippingInputPanel';
import OfficialTag from '@/components/OfficialTag';

import { submitSaleShipment } from '@/api/sales';
import { SALE_STATUS, CANCELLATION_REASON, getSaleStatusStepIndex, isSaleCancelled, getSaleStepperLabel, getSaleMessageTitle, getSaleMessageTitleKey, getSaleExplanationKey, getCancellationReasonKey } from '@/config/saleStatus';
import sellerShippingIcon from '@/assets/status_seller_shipping.svg';
import shippingIcon from '@/assets/status_purchased.svg';
import receivedIcon from '@/assets/status_received.svg';
import verifyingIcon from '@/assets/status_verifying.svg';
import completedIcon from '@/assets/status_done.svg';
import canceledIcon from '@/assets/status_canceled.svg';
import closeIcon from '@/assets/close.svg';
import copyIcon from '@/assets/copy.svg';
import warningPenaltyIcon from '@/assets/warning_red.svg';
import warningGrayIcon from '@/assets/warning_gray.svg';
import { formatDate, formatCurrency } from '@/utils/formatUtils';
import { calculateCountdown } from '@/utils/countdownUtils';
import { getPenaltyDisplay } from '@/utils/penaltyUtils';
import { openPenaltyPaymentModal } from '@/stores/penaltyPaymentStore';
import { getProductThumbnailUrl } from '@/utils/imageUtils';


const SaleDetailsModal = ({ isOpen, onClose, sale, openShippingPanel = false, onWithdrawClick }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isShippingPanelOpen, setIsShippingPanelOpen] = useState(openShippingPanel);
  const [updatedSale, setUpdatedSale] = useState(sale);
  const penalty = updatedSale?.penalty;
  const hasOutstandingPenalty = penalty?.status === 'OUTSTANDING';
  const isPenaltySettled = penalty?.status === 'SETTLED';
  const penaltyDisplay = hasOutstandingPenalty ? getPenaltyDisplay(penalty, t, { sale: updatedSale }) : null;
  const warehouseShipment = updatedSale?.warehouseShipment;

  const penaltyHeadlineText = hasOutstandingPenalty 
    ? t('sales.penalty.detail.title', {
        amount: penaltyDisplay.amountLabel || 0,
        dueAt: penaltyDisplay.dueLabel
      })
    : '';

  const penaltyDescriptionText = hasOutstandingPenalty
    ? penaltyDisplay?.reasonLabel : '';

  const penaltySettledHeadlineText = isPenaltySettled
    ? t('sales.penalty.detail.settledTitle', {
        amount: formatCurrency(penalty?.paidAmount ?? penalty?.outstandingAmount ?? 0),
      })
    : '';

  const penaltySettledDisplay = isPenaltySettled ? getPenaltyDisplay(penalty, t, { sale: updatedSale }) : null;

  const penaltySettledDescriptionText = isPenaltySettled && penaltySettledDisplay
    ? penaltySettledDisplay?.reasonLabel : '';

  // Calculate countdown for PAID status using shared utility
  const countdownInfo = useMemo(() => {
    return calculateCountdown(sale, t);
  }, [sale, t]);

  // Handle withdraw button click
  const handleWithdrawClick = () => {
    onClose(); // Close the SaleDetailsModal first
    if (onWithdrawClick) {
      onWithdrawClick(); // Then trigger the parent's withdraw modal
    }
  };

  const handlePayPenalty = () => {
    onClose();
    openPenaltyPaymentModal();
  };

  if (!isOpen || !sale) return null;

  // Helper to get the sale message title based on status
  const getSaleMessageTitleText = (status, isOfficial) => {
    return t(getSaleMessageTitleKey(status, isOfficial));
  };

  // Helper to get the sale explanation based on status
  const isCancelled = isSaleCancelled(sale.status);
  const getSaleExplanation = (status, isOfficial, cancellationReason, verificationNote) => {
    return isCancelled ? cancellationReason === CANCELLATION_REASON.VERIFICATION_FAILED && verificationNote ? t(getCancellationReasonKey(cancellationReason, verificationNote), {reason: verificationNote}) : t(getCancellationReasonKey(cancellationReason, verificationNote)) : t(getSaleExplanationKey(status, isOfficial));
  };

  // Helper to format status steps
  const getStatusStep = (status, isOfficial) => {
    const currentStepIndex = getSaleStatusStepIndex(status, isOfficial);
    
    // Define the stepper labels in order based on sale statuses
    const stepLabels = isOfficial ? [
      t('sales.status.official_paid'),
      t('sales.status.completed')
    ] : [
      t('sales.status.paid'),
      t('sales.status.seller_shipped'),
      t('sales.status.warehouse_received'),
      t('sales.status.verifying'),
      t('sales.status.completed')
    ];
    
    return stepLabels.map((label, index) => ({
      label: label,
      completed: index <= currentStepIndex,
      active: index === currentStepIndex,
    }));
  };

  const getStatusIcon = (status, isOfficial) => {
    if (status != SALE_STATUS.CANCELLED && isOfficial && getSaleStatusStepIndex(status, isOfficial) === 0) {
      return sellerShippingIcon;
    } else {
      switch (status) {
        case SALE_STATUS.PAID:
          return sellerShippingIcon;
        case SALE_STATUS.SELLER_SHIPPED:
          return shippingIcon;
        case SALE_STATUS.WAREHOUSE_RECEIVED:
          return receivedIcon;
        case SALE_STATUS.VERIFYING:
          return verifyingIcon;
        case SALE_STATUS.COMPLETED:
          return completedIcon;
        case SALE_STATUS.CANCELLED:
          return canceledIcon;
        default:
          return null;
      }
    }
  }

  const statusIcon = getStatusIcon(sale.status, sale.listing?.isOfficial);
  const statusSteps = getStatusStep(sale.status, sale.listing?.isOfficial);
  const stepperLabel = t(getSaleStepperLabel(sale.status, sale.listing?.isOfficial));
  const messageTitle = sale.status === SALE_STATUS.PAID && countdownInfo?.timeLeft
    ? t(getSaleMessageTitle(sale.status), { time: countdownInfo.timeLeft })
    : getSaleMessageTitleText(sale.status, sale.listing?.isOfficial);
  const explanation = getSaleExplanation(sale.status, sale.listing?.isOfficial, sale.cancellationReason, sale.verificationNote);

  // Handler for ship now button
  const handleShipNow = () => {
    setIsShippingPanelOpen(true);
  };

  // Function to handle shipping submission
  const handleShippingSubmit = async (shippingData) => {
    try {
      const response = await submitSaleShipment(sale.id, shippingData);
      setUpdatedSale(response.data);
      setIsShippingPanelOpen(false);
      // Invalidate the sales query to refresh the list
      queryClient.invalidateQueries(['sales']);
    } catch (error) {
      console.error('Error submitting shipment:', error);
      // TODO: Add error handling UI
    }
  };

  const handleContactClick = () => {
    onClose();
    navigate('/contact');
  };

  // Function to copy tracking number to clipboard
  const copyTrackingNumber = async () => {
    if (sale.sellerShipment?.trackingNumber) {
      try {
        await navigator.clipboard.writeText(sale.sellerShipment.trackingNumber);
        toast(t('orders.trackingNumberCopied'));
      } catch (error) {
        console.error('Failed to copy tracking number:', error);
        // Fallback for older browsers or when clipboard API fails
        toast(t('orders.trackingNumberCopied'));
      }
    }
  };

  // Get the appropriate status message UI based on sale status
  const renderStatusMessage = () => {
    switch (sale.status) {
      case SALE_STATUS.PAID:
        if (!countdownInfo) return null;
        return (
          <div className="self-stretch p-5 bg-[#f7f7f7] rounded-md outline outline-1 outline-offset-[-1px] outline-[#eeeeee] inline-flex flex-col justify-center items-center gap-5">
            <div className="flex flex-col justify-start items-center gap-2">
                <div className="justify-center">
                  <span className="text-[#111111] text-lg font-bold leading-[27px]">{t('sales.countdown.title')} </span>
                  <span className="text-[#3b5493] text-lg font-bold leading-[27px]">{countdownInfo.timeLeft}</span>
                </div>
                <div className="w-[392px] text-center justify-center text-[#666666] text-sm font-normal leading-[21px]">
                  {t('sales.countdown.message', { deadline: countdownInfo.deadline })}
                </div>
            </div>
            <Button
              onClick={handleShipNow}
              variant="primary"
              size="l"
              className="self-stretch"
            >
              {t('sales.countdown.shipNow')}
            </Button>
          </div>
        );
      
      case SALE_STATUS.SELLER_SHIPPED:
        return (
          <div className="self-stretch p-5 bg-[#f7f7f7] rounded-md outline outline-1 outline-offset-[-1px] outline-[#eeeeee] inline-flex flex-col justify-center items-center gap-5">
            <div className="self-stretch flex flex-col justify-start items-center gap-2">
              <h3 className="text-lg font-bold text-[#111111]">{messageTitle}</h3>
              <p className="text-sm text-[#666666] text-center">{explanation}</p>
              
              <div className="self-stretch inline-flex justify-between items-center mt-2">
                <div className="flex justify-center items-center gap-2">
                  <span className="text-[#666666] text-sm">{t('orders.shippingCompany')}:</span>
                  <span className="text-[#111111] text-sm">{sale.sellerShipment?.courierName}</span>
                </div>
                <div className="flex justify-center items-center gap-2">
                  <span className="text-[#666666] text-sm">{t('orders.trackingNumber')}:</span>
                  <span className="text-[#111111] text-sm">{sale.sellerShipment?.trackingNumber || '-'}</span>
                  <button onClick={copyTrackingNumber} className="w-3 h-3 flex items-center justify-center">
                    <img src={copyIcon} alt="copy" className="w-[11px] h-[11px]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case SALE_STATUS.COMPLETED:
        return (
          <div className="self-stretch p-5 bg-[#f7f7f7] rounded-md outline outline-1 outline-offset-[-1px] outline-[#eeeeee] inline-flex flex-col justify-center items-center gap-4">
            <div className="flex flex-col justify-start items-center gap-2">
              <div className="justify-center text-[#111111] text-lg font-bold leading-[27px]">
                {t('sales.completed.title')}
              </div>
              <div className="w-[392px] text-center justify-center text-[#666666] text-sm font-normal leading-[21px]">
                {t('sales.completed.message')}
              </div>
            </div>
            <Button
              onClick={handleWithdrawClick}
              variant="primary"
              size="l"
              className="self-stretch"
            >
              {t('sales.completed.withdrawButton')}
            </Button>
          </div>
        );
      
      default:
        return (
          <div className="w-full p-5 border border-gray-200 bg-[#f8f8f8] rounded-md">
            <h3 className="text-xl font-bold mb-2 text-center">{messageTitle}</h3>
            <p className="text-sm text-gray-500 text-center">{explanation}</p>
          </div>
        );
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black/60 flex flex-col justify-center items-end !border-t-0 z-50">
      <div className="w-[480px] h-full bg-white flex flex-col items-center gap-6 p-6 relative overflow-y-auto">
        {/* Header */}
        <div className="w-full flex flex-cols items-center justify-between">
            <div className='flex items-center'>
                {statusIcon && (
                    <img src={statusIcon} alt={sale.status} className="w-4 h-4" />
                )}
                <h2 className="text-lg font-bold ml-2">
                    {stepperLabel}
                </h2>
            </div>
            <button 
                onClick={onClose}
                className="w-6 h-6 flex items-center justify-center"
            >
                <img src={closeIcon} alt="close" className="w-7 h-7" />
            </button>
        </div>

        {/* Product Image and Info */}
        <div className="w-full flex gap-4">
            <div className="w-32 h-32 rounded overflow-hidden">
              <img 
              src={getProductThumbnailUrl(sale.listing.product.images?.[0]?.url)} 
              alt={updatedSale.listing.product?.localizedName || "Product"} 
              className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1">
                <div className="mb-1">
                  <div className="min-w-0">
                    {updatedSale.listing.isOfficial && (
                      <OfficialTag className="align-middle mr-1.5" />
                    )}
                    <h3 className="inline align-middle text-base font-medium whitespace-normal break-words min-w-0">
                      {updatedSale.listing.product?.localizedRawName || updatedSale.listing.product?.localizedName || ""}
                      {updatedSale.listing?.product?.rarityInfo?.sub?.name ? `(${updatedSale.listing.product.rarityInfo.sub.name})` : ''}
                    </h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    {updatedSale.listing?.product?.rarityInfo?.main && (
                      <RarityBadge rarity={updatedSale.listing.product.rarityInfo.main} />
                    )}
                    {(updatedSale.listing?.product?.set?.code || (updatedSale.listing?.product?.rawCode && updatedSale.listing?.product?.type !== 'CARD_BOX')) && (
                      <div className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] flex justify-start items-center gap-2.5">
                        <div className="justify-start text-[#111111] text-sm font-normal leading-[14px]">
                          {updatedSale.listing?.product?.type === 'CARD_BOX' ? updatedSale.listing?.product?.set?.code : (updatedSale.listing?.product?.set?.code && updatedSale.listing?.product?.rawCode ? `${updatedSale.listing.product.set.code} ${updatedSale.listing.product.rawCode}` : updatedSale.listing?.product?.set?.code || updatedSale.listing?.product?.rawCode)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-sm mt-6">
                    <span className="text-gray-500">
                        {updatedSale.listing?.psa ?
                            t('psa.grade') :
                            updatedSale.listing?.bundledQuantity ?
                                t('box.quantity') :
                                t('listing.condition')}:
                    </span>
                    <span className="ml-2">
                        {updatedSale.listing?.psa ?
                            updatedSale.listing.psa.replace('PSA_', 'PSA ') :
                            updatedSale.listing?.bundledQuantity ?
                                (() => {
                                    const unit = updatedSale.listing.product?.boxType === 'PACK' ? t('box.pack') : t('box.box_unit');
                                    return `${updatedSale.listing.bundledQuantity} ${unit}`;
                                })() :
                                updatedSale.listing?.condition ?
                                    t(`sell.product_info.conditions.${updatedSale.listing.condition.toLowerCase()}`) :
                                    '-'}
                    </span>
                </div>
            </div>
        </div>

        {/* Status Steps - Using the Stepper component - Hide for cancelled sales */}
        {!isCancelled && (
          <div className="w-full">
            <Stepper steps={statusSteps} useBlue activeColor='bg-[#3b5493]'/>
          </div>
        )}

        {/* Status Message Block - Uses the renderStatusMessage function */}
        {!hasOutstandingPenalty && (!isPenaltySettled || !isCancelled) && renderStatusMessage()}

        {isCancelled && warehouseShipment && (
          <div className="w-full p-4 bg-[#f7f7f7] border border-[#eeeeee] rounded-md flex flex-col gap-2">
            <span className="text-sm font-bold text-[#111111]">
              {t('sales.deliveryInfo.title')}
            </span>
            {warehouseShipment.courierName && (
              <div className="flex justify-between text-sm text-[#111111]">
                <span>{t('sales.deliveryInfo.courierLabel')}</span>
                <span>{warehouseShipment.courierName}</span>
              </div>
            )}
            {!warehouseShipment.courierName && warehouseShipment.courier && (
              <div className="flex justify-between text-sm text-[#111111]">
                <span>{t('sales.deliveryInfo.courierLabel')}</span>
                <span>{t(`sales.deliveryInfo.courier.${warehouseShipment.courier.toLowerCase()}`)}</span>
              </div>
            )}
            {warehouseShipment.trackingNumber && (
              <div className="flex justify-between text-sm text-[#111111]">
                <span>{t('sales.deliveryInfo.trackingLabel')}</span>
                <span>{warehouseShipment.trackingNumber}</span>
              </div>
            )}
          </div>
        )}

        {/* Outstanding Penalty Notice */}
        {hasOutstandingPenalty && penaltyDisplay && (
          <div className="w-full flex flex-col gap-3">
            <div className="p-4 bg-[#ffedea] border border-[#ffe5df] rounded-md flex items-start gap-2">
              <img src={warningPenaltyIcon} alt="warning" className="w-5 h-5" />
              <div
                className="self-stretch justify-center flex flex-col gap-2"
              >
                <span className="text-[#ff2b00] text-base font-bold leading-normal whitespace-pre-line">
                  {penaltyHeadlineText}
                  <br />
                </span>
                <span className="text-[#ff2b00] text-sm font-normal leading-[21px]">
                  {penaltyDescriptionText}
                </span>
              </div>
            </div>
            <Button variant="primary" size="l" className="self-stretch mt-4" onClick={handlePayPenalty}>
              {t('sales.penalty.detail.payAction')}
            </Button>
          </div>
        )}

        {/* Settled Penalty Notice */}
        {isPenaltySettled && isCancelled && (
          <div className="w-full p-3 bg-[#f7f7f7] rounded-md outline outline-1 outline-offset-[-1px] outline-[#eeeeee] inline-flex items-start gap-2">
            <div className="w-4 h-4 flex items-center justify-center mt-1">
              <img src={warningGrayIcon} alt="penalty settled" className="w-4 h-4" />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <span className="text-[#aaaaaa] text-base font-bold leading-normal whitespace-pre-line">
                {penaltySettledHeadlineText}
              </span>
              <span className="text-[#aaaaaa] text-sm font-normal leading-[21px]">
                {penaltySettledDescriptionText}
              </span>
            </div>
          </div>
        )}

        {/* Price Information */}
        <div className="w-full gap-4 text-[#111111]">
            <div className="flex justify-between items-center mb-3">
                <span className="text-sm">{t('sales.productPrice')}</span>
                <span className="text-sm">{formatCurrency(updatedSale.monetary.price || 0)}</span>
            </div>
            <div className="flex justify-between items-center mb-3">
                <span className="text-sm">{t('sales.transactionFee')}</span>
                <span className="text-sm">{formatCurrency(updatedSale.monetary.commissionFee || 0)}</span>
            </div>
            <div className="flex justify-between items-center font-bold py-2">
                <span className="text-base">{t('sales.totalEarnings')}</span>
                <span className="text-base">{formatCurrency(updatedSale.monetary.revenue || 0)}</span>
            </div>
        </div>

        <div className='w-full h-[1px] bg-[#f4f4f4]' />

        {/* Order Information */}
        <div className="w-full">
            <h3 className="text-sm font-bold mb-4">{t('sales.saleContents')}</h3>
            <div className="space-y-2">
                <div className="flex">
                    <span className="text-sm text-gray-500 w-36">{t('sales.shippingAddress')}</span>
                    <span className="text-sm flex-1">{updatedSale.warehouseAddress || '-'}</span>
                </div>
                <div className="flex">
                    <span className="text-sm text-gray-500 w-36">{t('sales.transactionNumber')}</span>
                    <span className="text-sm flex-1">{updatedSale.number || '-'}</span>
                </div>
                <div className="flex">
                    <span className="text-sm text-gray-500 w-36">{t('sales.transactionDate')}</span>
                    <span className="text-sm flex-1">
                        {updatedSale.createdAt ? formatDate(updatedSale.createdAt) : '-'}
                    </span>
                </div>
                {updatedSale.finishedAt && (
                    <div className="flex">
                        <span className="text-sm text-gray-500 w-36">{t('common.finishDate')}</span>
                        <span className="text-sm flex-1">
                            {formatDate(updatedSale.finishedAt)}
                        </span>
                    </div>
                )}
                <div className="flex">
                    <span className="text-sm text-gray-500 w-36">{t('sales.merchant')}</span>
                    <span className="text-sm flex-1">{updatedSale.registeredBusiness}</span>
                </div>
            </div>
        </div>

        <Button
            onClick={handleContactClick}
            variant="default"
            size="l"
            className="py-2"
        >
            {t('sales.contactSupport')}
        </Button>

        {/* Shipping Input Panel */}
        <ShippingInputPanel
          isOpen={isShippingPanelOpen}
          onClose={() => setIsShippingPanelOpen(false)}
          onSubmit={handleShippingSubmit}
          sale={updatedSale}
        />
      </div>
    </div>


  </>
  );
};

export default SaleDetailsModal; 
